// components/media/Trash.js
import React from 'react';
import {
  Card,
  Typography,
  Badge,
  Button,
  Space,
  Table,
  Alert,
  Modal,
  Image,
  message
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PictureOutlined,
  FileOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { deleteMedia, restoreMedia } from '../../features/media/mediaSlice';

const { Text } = Typography;

const Trash = ({ onEditMedia }) => {
  const dispatch = useDispatch();
  const { media, pagination, loading } = useSelector((state) => state.media);

  // Delete media file permanently
  const handleDeleteMedia = async (mediaId, permanent = false) => {
    try {
      await dispatch(deleteMedia({ id: mediaId, permanent })).unwrap();
      message.success(permanent ? 'Media permanently deleted' : 'Media deleted successfully');
    } catch (err) {
      message.error('Failed to delete media');
    }
  };

  // Restore deleted media
  const handleRestoreMedia = async (mediaId) => {
    try {
      console.log('Attempting to restore media ID:', mediaId);
      console.log('Making request to:', `/admin/media/${mediaId}/restore`);
      
      const result = await dispatch(restoreMedia(mediaId)).unwrap();
      console.log('Restore result:', result);
      message.success('Media restored successfully');
    } catch (err) {
      console.error('Full restore error object:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      message.error(`Failed to restore media: ${err.message || err}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'image':
        return <PictureOutlined />;
      case 'video':
        return <FileOutlined />;
      case 'document':
        return <FileOutlined />;
      default:
        return <FileOutlined />;
    }
  };

  const columns = [
    {
      title: 'Preview',
      dataIndex: 'url',
      key: 'preview',
      width: 100,
      render: (url, record) => (
        <div 
          onClick={() => {
            if (record.mediaType === 'image') {
              Modal.info({
                title: record.originalName,
                content: (
                  <Image
                    width="100%"
                    src={record.url}
                    alt={record.altText}
                  />
                ),
                width: 600,
              });
            } else {
              window.open(record.url, '_blank');
            }
          }}
          style={{ cursor: 'pointer' }}
          title="Click to preview"
        >
          {record.mediaType === 'image' ? (
            <Image
              width={60}
              height={60}
              src={url}
              alt={record.altText}
              style={{ objectFit: 'cover', borderRadius: '6px' }}
              preview={false}
            />
          ) : (
            <div className="w-15 h-15 bg-gray-100 rounded flex items-center justify-center">
              {getMediaTypeIcon(record.mediaType)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'File Name',
      dataIndex: 'originalName',
      key: 'originalName',
      ellipsis: true,
    },
    {
      title: 'Entity',
      key: 'entity',
      render: (_, record) => (
        <div>
          <Badge color="blue" text={record.entityType} />
          <br />
          <Text type="secondary" className="text-xs">
            {record.entityId?.name || record.entityId?.title || 'Unknown'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'mediaType',
      key: 'mediaType',
      render: (type) => (
        <Badge color="green" text={type} />
      ),
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size) => formatFileSize(size),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'active' ? 'green' : status === 'deleted' ? 'red' : 'orange';
        return <Badge color={color} text={status} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small" direction="vertical" style={{ width: '100%' }}>
          {record.status === 'deleted' ? (
            <>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleRestoreMedia(record._id)}
                type="primary"
                ghost
                style={{ width: '100%', fontSize: '11px' }}
                title="Restore"
              />
              <Button
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={() => {
                  Modal.confirm({
                    title: 'Permanently Delete Media',
                    content: 'Are you sure you want to permanently delete this media file? This action cannot be undone.',
                    okText: 'Delete Permanently',
                    okType: 'danger',
                    onOk: () => handleDeleteMedia(record._id, true)
                  });
                }}
                style={{ width: '100%', fontSize: '11px' }}
                title="Delete Permanently"
              />
            </>
          ) : (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEditMedia(record)}
              style={{ width: '100%' }}
              title="Edit"
            />
          )}
        </Space>
      ),
    },
  ];

  const deletedMedia = media.filter(item => item.status === 'deleted');

  return (
    <div>
      <Alert
        message="Trash"
        description="Deleted media files are stored here. You can restore or permanently delete them."
        type="warning"
        className="mb-4"
      />
      
      <Card>
        <div className="mb-4">
          <Text strong>Deleted Media Files</Text>
        </div>
        
        <Table
          columns={columns}
          dataSource={deletedMedia}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: deletedMedia.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} deleted items`,
          }}
          locale={{
            emptyText: loading ? 'Loading...' : 'No deleted items found. Use the Refresh button above to reload.'
          }}
        />
      </Card>
    </div>
  );
};

export default Trash;